import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { emailAPI, attachmentsAPI } from '../../services/api';
import { useOffline } from '../../context/OfflineContext';
import { sanitizeHtml } from '../../lib/sanitize';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Send,
  Paperclip,
  X,
  Save,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// ── Configuration de la barre d'outils Quill ────────────────────────────────
// N'utilise PAS document.execCommand() — Quill gère tout via son propre delta.
const QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      ['link'],
      ['clean'],
    ],
  },
  clipboard: {
    // Colle du HTML propre sans styles parasites
    matchVisual: false,
  },
};

const QUILL_FORMATS = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'indent',
  'link',
];

export const ComposeEmail = ({ open, onClose, signatures, replyTo, onSent }) => {
  const [loading, setLoading] = useState(false);
  const { isOnline, queueEmail, saveDraftOffline } = useOffline();
  const [emailData, setEmailData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body_html: '',
    body_text: '',
    signature_id: '',
  });
  const [attachments, setAttachments] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);

  // ── Pré-remplissage (réponse ou nouveau message) ─────────────────────────
  useEffect(() => {
    if (!open) return;

    if (replyTo) {
      const prefix = replyTo.subject?.startsWith('Re:') ? '' : 'Re: ';
      // Sanitisation du corps original avant injection dans l'éditeur
      const safeBody = sanitizeHtml(replyTo.body_html || replyTo.body_text || '');
      const dateStr = replyTo.date && !isNaN(new Date(replyTo.date))
        ? new Date(replyTo.date).toLocaleDateString('fr-FR')
        : '';
      const replyBody = `<p><br></p><p><br></p><hr><p>Le ${dateStr}, ${replyTo.from_address} a écrit&nbsp;:</p><blockquote style="border-left:3px solid #ccc;padding-left:12px;margin-left:0;color:#555;">${safeBody}</blockquote>`;

      setEmailData({
        to: replyTo.from_address || '',
        cc: '',
        bcc: '',
        subject: `${prefix}${replyTo.subject || ''}`,
        body_html: replyBody,
        body_text: '',
        signature_id: signatures.find(s => s.is_default)?.id || '',
      });
    } else {
      setEmailData({
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body_html: '',
        body_text: '',
        signature_id: signatures.find(s => s.is_default)?.id || '',
      });
    }
  }, [replyTo, open, signatures]);

  // ── Callback Quill onChange ───────────────────────────────────────────────
  // Quill appelle onChange(html, delta, source, editor) à chaque frappe
  const handleEditorChange = useCallback((html, _delta, _source, editor) => {
    setEmailData(prev => ({
      ...prev,
      body_html: html,
      body_text: editor.getText(),
    }));
  }, []);

  // ── Envoi ─────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!emailData.to.trim()) {
      toast.error('Veuillez saisir un destinataire');
      return;
    }

    const emailPayload = {
      to: emailData.to.split(',').map(e => e.trim()).filter(Boolean),
      cc: emailData.cc ? emailData.cc.split(',').map(e => e.trim()).filter(Boolean) : [],
      bcc: emailData.bcc ? emailData.bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
      subject: emailData.subject,
      body_html: emailData.body_html || `<p>${emailData.body_text}</p>`,
      body_text: emailData.body_text,
      signature_id: emailData.signature_id || null,
      attachments: attachments.map(a => ({ filename: a.name, attachment_id: a.id })),
    };

    if (!isOnline) {
      await queueEmail(emailPayload);
      onClose();
      return;
    }

    setLoading(true);
    try {
      await emailAPI.sendEmail(emailPayload);
      toast.success('Email envoyé !');
      onSent?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  // ── Brouillon ─────────────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (!isOnline) {
      await saveDraftOffline({
        to: emailData.to,
        cc: emailData.cc,
        subject: emailData.subject,
        body_html: emailData.body_html,
        body_text: emailData.body_text,
      });
      onClose();
      return;
    }

    setLoading(true);
    try {
      await emailAPI.saveDraft({
        to: emailData.to ? emailData.to.split(',').map(e => e.trim()).filter(Boolean) : [],
        cc: emailData.cc ? emailData.cc.split(',').map(e => e.trim()).filter(Boolean) : [],
        bcc: emailData.bcc ? emailData.bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
        subject: emailData.subject,
        body_html: emailData.body_html,
        body_text: emailData.body_text,
      });
      toast.success('Brouillon sauvegardé');
      onClose();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  // ── Pièces jointes ────────────────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const MAX_SIZE = 25 * 1024 * 1024; // 25 Mo

    for (const file of files) {
      if (file.size > MAX_SIZE) {
        toast.error(`${file.name} dépasse la taille maximale de 25 Mo`);
        continue;
      }
      try {
        const res = await attachmentsAPI.upload(file);
        setAttachments(prev => [...prev, {
          id: res.data.id,
          name: res.data.filename,
          size: res.data.size,
          content_type: res.data.content_type,
        }]);
      } catch {
        toast.error(`Erreur d'upload pour ${file.name}`);
      }
    }
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / 1048576).toFixed(1)} Mo`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-4 border-b border-border flex-shrink-0">
          <DialogTitle className="font-['Plus_Jakarta_Sans']">
            {replyTo ? 'Répondre' : 'Nouveau message'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Destinataires */}
          <div className="p-4 space-y-3 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-3">
              <Label className="w-12 text-right text-muted-foreground">À</Label>
              <Input
                placeholder="destinataire@email.com"
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                className="flex-1"
                data-testid="compose-to-input"
              />
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCc(!showCc)}
                  className={cn(showCc && 'bg-muted')}
                >
                  Cc
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowBcc(!showBcc)}
                  className={cn(showBcc && 'bg-muted')}
                >
                  Cci
                </Button>
              </div>
            </div>

            {showCc && (
              <div className="flex items-center gap-3">
                <Label className="w-12 text-right text-muted-foreground">Cc</Label>
                <Input
                  placeholder="cc@email.com"
                  value={emailData.cc}
                  onChange={(e) => setEmailData({ ...emailData, cc: e.target.value })}
                  className="flex-1"
                  data-testid="compose-cc-input"
                />
              </div>
            )}

            {showBcc && (
              <div className="flex items-center gap-3">
                <Label className="w-12 text-right text-muted-foreground">Cci</Label>
                <Input
                  placeholder="cci@email.com"
                  value={emailData.bcc}
                  onChange={(e) => setEmailData({ ...emailData, bcc: e.target.value })}
                  className="flex-1"
                  data-testid="compose-bcc-input"
                />
              </div>
            )}

            <div className="flex items-center gap-3">
              <Label className="w-12 text-right text-muted-foreground">Objet</Label>
              <Input
                placeholder="Objet du message"
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                className="flex-1"
                data-testid="compose-subject-input"
              />
            </div>
          </div>

          {/* Bouton pièce jointe (séparé de la toolbar Quill) */}
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/20 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs text-muted-foreground"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="w-3.5 h-3.5" />
              Joindre un fichier
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              multiple
              onChange={handleFileSelect}
            />
            <div className="flex-1" />
            {signatures.length > 0 && (
              <Select
                value={emailData.signature_id || 'none'}
                onValueChange={(v) => setEmailData({ ...emailData, signature_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger className="w-40 h-7 text-xs">
                  <SelectValue placeholder="Signature" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune signature</SelectItem>
                  {signatures.map((sig) => (
                    <SelectItem key={sig.id} value={sig.id}>
                      {sig.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Éditeur Quill — remplace contentEditable + document.execCommand() */}
          <div className="flex-1 overflow-hidden quill-compose-wrapper" data-testid="compose-body-editor">
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={emailData.body_html}
              onChange={handleEditorChange}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Rédigez votre message..."
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            />
          </div>

          {/* Pièces jointes */}
          {attachments.length > 0 && (
            <div className="p-3 border-t border-border flex-shrink-0">
              <div className="flex flex-wrap gap-2">
                {attachments.map((file, index) => (
                  <div
                    key={file.id || index}
                    className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md text-sm"
                    data-testid={`attachment-${index}`}
                  >
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="truncate max-w-[150px]">{file.name}</span>
                    <span className="text-xs text-muted-foreground">{formatSize(file.size)}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-5 h-5"
                      onClick={() => removeAttachment(index)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Pied de page */}
        <div className="p-4 border-t border-border flex items-center justify-between flex-shrink-0">
          <Button variant="outline" onClick={handleSaveDraft} disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            Brouillon
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button onClick={handleSend} disabled={loading} data-testid="send-email-btn">
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'Envoi...' : 'Envoyer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ComposeEmail;
